import { connectAdvanced } from 'react-redux'
import SelfIncrementingProgressbar, {
  PROGRESSBAR_TICK,
  SelfIncrementingProgressbarProps,
} from 'components/features/pfm/SelfIncrementingProgressbar/SelfIncrementingProgressbar'
import { LinkageProgress, MixedCompositeId } from 'interfaces/linkage'
import { createLinkageProgressSelector, createProviderByCidSelector } from 'selectors/linkageSelectors'
import { createSelector } from 'reselect'
import { renderOptionally, OptionalRendererProps } from 'tangle-shared'
import { State } from 'interfaces/state'
import { QAMarked, StoreDispatch } from 'interfaces/component'
import { setLinkageProgressActionCreator } from 'actions/linkageActions'
import { createOptionalRenderer } from 'helpers/tangleSharedHelpers'

type SelfIncrementingProgressbarContainerOwnProps =
  & QAMarked
  & {
    cid: MixedCompositeId
    isCompact?: boolean
    hideValue?: boolean
  }

const MAXIMUM_FINISHED_PERCENTAGE = 100 // percents
const MAXIMUM_LOADING_PERCENTAGE = 99 // percents
const DEFAULT_LINKAGE_TIME = 135 // seconds
// time needs to a progressbar be filled to 100% from whatever current progress value
const PROGRESSBAR_COMPLETION_TIME = 600 // milliseconds

const selectorFactory = (dispatch: StoreDispatch) => {
  const dependenciesSelector = createSelector(
    createLinkageProgressSelector(),
    createProviderByCidSelector(),
    (linkageProgress, provider) => ({
      linkageProgress,
      provider,
    })
  )

  let lastLinkageProgress: LinkageProgress | null = null
  let lastProps: OptionalRendererProps<SelfIncrementingProgressbarProps> | null = null

  const optionalRenderer = createOptionalRenderer<SelfIncrementingProgressbarProps>()
  return (nextState: State, ownProps: SelfIncrementingProgressbarContainerOwnProps): OptionalRendererProps<SelfIncrementingProgressbarProps> => {
    const { linkageProgress, provider } = dependenciesSelector(nextState, ownProps)

    if (!linkageProgress || linkageProgress.state === 'stopped' || !provider) {
      return optionalRenderer.fallback()
    }

    if (!lastProps || lastLinkageProgress !== linkageProgress) {
      const { value, state, cid } = linkageProgress

      const isLoading = state === 'loading'

      const overallLinkageTime = (provider.linkage_time || DEFAULT_LINKAGE_TIME) * 1000
      const percentageLeft = 100 - value
      const increment = isLoading
        ? (PROGRESSBAR_TICK / overallLinkageTime) * 100
        : percentageLeft / PROGRESSBAR_COMPLETION_TIME * PROGRESSBAR_TICK

      const initial: number = value
      const maximum: number = isLoading ? MAXIMUM_LOADING_PERCENTAGE : MAXIMUM_FINISHED_PERCENTAGE

      lastLinkageProgress = linkageProgress
      lastProps = optionalRenderer.render({
        isCompact: ownProps.isCompact,
        hideValue: ownProps.hideValue,
        qaMarker: ownProps.qaMarker,
        qaType: ownProps.qaType,
        increment,
        initial,
        maximum,

        onFinish: () => dispatch(setLinkageProgressActionCreator(cid, null)),
        onUnmount: (progress: number) => dispatch(setLinkageProgressActionCreator(cid, progress)),
      })

      return lastProps
    }

    return lastProps
  }
}

const SelfIncrementingProgressbarContainer = connectAdvanced<
  State, OptionalRendererProps<SelfIncrementingProgressbarProps>, SelfIncrementingProgressbarContainerOwnProps
>(selectorFactory)(
  renderOptionally(SelfIncrementingProgressbar)
)

export default SelfIncrementingProgressbarContainer
